import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider,
  Paper,
  Button,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Warning,
  Info,
  Download,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { api } from '../services/api';

interface AnalysisDisplayProps {
  analysisData: any;
  reportId: string | null;
}

const AnalysisDisplay: React.FC<AnalysisDisplayProps> = ({ analysisData, reportId }) => {
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  if (!analysisData) {
    return (
      <Alert severity="info">
        No analysis data available. Please upload a report first.
      </Alert>
    );
  }

  const { biomarkers, summary_counts, age, gender } = analysisData;
  
  // Function to determine biomarker status based on value and reference range
  const determineBiomarkerStatus = (biomarker: any) => {
    const value = parseFloat(biomarker.value);
    const range = biomarker.used_range;
    
    if (!range || isNaN(value)) return 'RANGE_UNKNOWN';
    
    // Parse reference range (e.g., "13.5 - 17.5", "< 5.7", "> 40")
    const rangeStr = range.toString().trim();
    
    if (rangeStr.includes(' - ')) {
      // Range format: "13.5 - 17.5"
      const [min, max] = rangeStr.split(' - ').map((v: string) => parseFloat(v.trim()));
      if (value < min) return 'LOW';
      if (value > max) return 'HIGH';
      return 'NORMAL';
    } else if (rangeStr.startsWith('<')) {
      // Less than format: "< 5.7"
      const threshold = parseFloat(rangeStr.substring(1).trim());
      if (value >= threshold) return 'HIGH';
      return 'NORMAL';
    } else if (rangeStr.startsWith('>')) {
      // Greater than format: "> 40"
      const threshold = parseFloat(rangeStr.substring(1).trim());
      if (value <= threshold) return 'LOW';
      return 'NORMAL';
    }
    
    return 'RANGE_UNKNOWN';
  };

  // Update biomarkers with proper status
  const updatedBiomarkers = biomarkers.map((bio: any) => ({
    ...bio,
    status: determineBiomarkerStatus(bio)
  }));

  const abnormalBiomarkers = updatedBiomarkers.filter((bio: any) => bio.status === 'LOW' || bio.status === 'HIGH');
  const normalBiomarkers = updatedBiomarkers.filter((bio: any) => bio.status === 'NORMAL');

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const response = await api.generatePdf(analysisData);
      
      if (response.data.success) {
        // Download the PDF
        const pdfUrl = await api.downloadPdf(response.data.pdf_filename);
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = response.data.pdf_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the blob URL
        URL.revokeObjectURL(pdfUrl);
        
        setSnackbarMessage('PDF report generated and downloaded successfully!');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(`Error generating PDF: ${error.response?.data?.detail || 'Unknown error'}`);
      setSnackbarOpen(true);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const statusColors = {
    LOW: '#ff9800',
    HIGH: '#f44336',
    NORMAL: '#4caf50',
    INVALID: '#9e9e9e',
    RANGE_UNKNOWN: '#9e9e9e',
  };

  const statusIcons = {
    LOW: <TrendingDown />,
    HIGH: <TrendingUp />,
    NORMAL: <CheckCircle />,
    INVALID: <Warning />,
    RANGE_UNKNOWN: <Info />,
  };

  // Update summary counts based on actual analysis
  const actualSummaryCounts = {
    total: updatedBiomarkers.length,
    normal: normalBiomarkers.length,
    abnormal: abnormalBiomarkers.length,
  };

  // Prepare data for charts
  const statusData = [
    { name: 'Normal', value: actualSummaryCounts.normal, color: '#4caf50' },
    { name: 'Abnormal', value: actualSummaryCounts.abnormal, color: '#f44336' },
  ];

  const biomarkerData = updatedBiomarkers.slice(0, 10).map((bio: any) => ({
    name: bio.name.length > 15 ? bio.name.substring(0, 15) + '...' : bio.name,
    value: parseFloat(bio.value) || 0,
    status: bio.status,
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Analysis Results
          </Typography>
          {reportId && (
            <Typography variant="body2" color="text.secondary">
              Report ID: {reportId}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={generatingPdf ? <CircularProgress size={20} /> : <Download />}
          onClick={handleGeneratePdf}
          disabled={generatingPdf}
          sx={{ minWidth: 160 }}
        >
          {generatingPdf ? 'Generating...' : 'Generate PDF'}
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Biomarkers
              </Typography>
              <Typography variant="h4">
                {actualSummaryCounts.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Normal
              </Typography>
              <Typography variant="h4" color="success.main">
                {actualSummaryCounts.normal}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Abnormal
              </Typography>
              <Typography variant="h4" color="error.main">
                {actualSummaryCounts.abnormal}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Biomarkers
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={biomarkerData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Abnormal Biomarkers */}
      {abnormalBiomarkers.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="error">
              ⚠️ Abnormal Biomarkers ({abnormalBiomarkers.length})
            </Typography>
            <List>
              {abnormalBiomarkers.map((bio: any, index: number) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <ListItemIcon>
                        {statusIcons[bio.status as keyof typeof statusIcons]}
                      </ListItemIcon>
                      <ListItemText
                        primary={bio.name}
                        secondary={`${bio.value} ${bio.unit || ''} (${bio.status})`}
                      />
                      <Chip
                        label={bio.status}
                        color={bio.status === 'HIGH' ? 'error' : 'warning'}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Reference Range:</strong> {bio.used_range || 'Not available'}
                      </Typography>
                      
                      {bio.rag && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            AI Analysis:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {bio.rag.summary}
                          </Typography>
                        </Box>
                      )}

                      {bio.gpt_check && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Validation: {bio.validation_aligned ? '✅ Aligned' : '❗ Conflict'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {bio.gpt_check.text}
                          </Typography>
                        </Box>
                      )}

                      {bio.rag?.references && bio.rag.references.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Research References:
                          </Typography>
                          <List dense>
                            {bio.rag.references.slice(0, 3).map((ref: any, refIndex: number) => (
                              <ListItem key={refIndex}>
                                <ListItemText
                                  primary={ref.title}
                                  secondary={`${ref.source} ${ref.year ? `(${ref.year})` : ''}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Normal Biomarkers */}
      {normalBiomarkers.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="success.main">
              ✅ Normal Biomarkers ({normalBiomarkers.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {normalBiomarkers.map((bio: any, index: number) => (
                <Chip
                  key={index}
                  label={`${bio.name}: ${bio.value} ${bio.unit || ''}`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AnalysisDisplay;
